import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, X, FileDown, TriangleAlert } from "lucide-react";
import provinces from "@doncicuto/es-provinces";
import municipalities from "@doncicuto/es-municipalities";
import { getServicios, getAaffListSuplier } from "../../services/proveedor.service";
import { Link } from "react-router-dom";
import FilterDropdown from "./FilterDropdown";

const FiltrosProveedoresLegalia = ({ onFilter, filtrosIniciales, user }) => {
  const { t } = useTranslation("common");
  const [busqueda, setBusqueda] = useState(filtrosIniciales || {});
  const [servicios, setServicios] = useState([]);
  const [listadoMunicipios, setListadoMunicipios] = useState([]);
  const [provCode, setProvCode] = useState("");
  const [countSinDocs, setCountSinDocs] = useState(null);

  useEffect(() => {
    getServicios().then((res) => {
      if (res?.data) setServicios(res.data);
    });
  }, []);

  useEffect(() => {
    getAaffListSuplier({ filtros: { sinDocumentos: true }, params: { page: 1, limit: 1 } })
      .then((res) => setCountSinDocs(res?.meta?.total ?? 0))
      .catch(() => setCountSinDocs(null));
  }, []);

  useEffect(() => {
    if (provCode) {
      const muns = municipalities
        .filter((m) => m.provCode === provCode)
        .sort((a, b) => a.name.localeCompare(b.name));
      setListadoMunicipios(muns);
    } else {
      setListadoMunicipios([]);
    }
  }, [provCode]);

  const handleChange = (name, value) => {
    const updated = { ...busqueda, [name]: value || undefined };
    if (name === "provincia" && !value) {
      delete updated.municipio;
    }
    for (const key in updated) {
      if (updated[key] === "" || updated[key] === null || updated[key] === undefined) {
        delete updated[key];
      }
    }
    setBusqueda(updated);
    onFilter(updated);
  };

  const selectStyle = { border: "1px solid #e5e7eb" };
  const inputBase =
    "py-1.5 px-3 text-sm rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-100 w-full sm:w-44";

  const sortedProvinces = [...provinces].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4">
      <FilterDropdown
        label={t("filters.province", "Provincia")}
        value={busqueda.provincia || ""}
        options={[
          { value: "", label: t("filters.all", "Todos") },
          ...sortedProvinces.map((p) => ({ value: p.name, label: p.name })),
        ]}
        onChange={(v) => {
          const selected = sortedProvinces.find(p => p.name === v);
          setProvCode(selected?.code || "");
          handleChange("provincia", v);
        }}
      />

      <FilterDropdown
        label={t("filters.municipality", "Municipio")}
        value={busqueda.municipio || ""}
        disabled={!busqueda.provincia}
        options={[
          { value: "", label: t("filters.all", "Todos") },
          ...listadoMunicipios.map((m) => ({ value: m.name, label: m.name })),
        ]}
        onChange={(v) => handleChange("municipio", v)}
      />

      <FilterDropdown
        label={t("filters.services", "Servicios")}
        value={busqueda.servicio || ""}
        options={[
          { value: "", label: t("filters.all", "Todos") },
          ...servicios.map((s) => ({ value: String(s.id), label: t(`serviceNames.${s.nombre}`, s.nombre) })).sort((a, b) => a.label.localeCompare(b.label)),
        ]}
        onChange={(v) => handleChange("servicio", v)}
      />

      {Object.keys(busqueda).length > 0 && (
        <button
          onClick={() => { setBusqueda({}); onFilter({}); }}
          className={`flex items-center gap-1 ${inputBase} text-red-500 hover:bg-red-50 cursor-pointer`}
          style={selectStyle}
        >
          <X className="w-3.5 h-3.5" />
          {t("actions.clear", "Limpiar")}
        </button>
      )}

      <div className="hidden sm:flex flex-1" />

      <button
        type="button"
        onClick={() => handleChange("sinDocumentos", busqueda.sinDocumentos ? undefined : true)}
        className={`flex items-center gap-2 px-4 py-1.5 text-sm font-semibold rounded-lg transition-colors cursor-pointer ${
          busqueda.sinDocumentos
            ? "bg-red-50 text-red-700"
            : "text-amber-700 hover:bg-amber-50"
        }`}
        style={{ border: busqueda.sinDocumentos ? "1px solid #fecaca" : "1px solid #e5e7eb" }}
      >
        <TriangleAlert className="w-4 h-4" />
        {t('filters.suppliersNoDocs', 'Prov. sin docs.')}
        {countSinDocs != null && (
          <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold ${
            busqueda.sinDocumentos ? "bg-red-200 text-red-800" : "bg-amber-100 text-amber-800"
          }`}>
            {countSinDocs}
          </span>
        )}
      </button>

      <Link
        to={`/doc-render/${user.nif}/situacion-proveedores`}
        target="_blank"
        className="flex items-center gap-2 px-4 py-1.5 text-sm font-semibold text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        style={{ border: "1px solid #e5e7eb" }}
      >
        {t('aaff:reports.downloadInform')}
        <FileDown className="w-4 h-4" />
      </Link>

      <div className="relative flex items-center w-full sm:w-auto">
        <Search className="absolute left-3 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          placeholder={t("actions.search") + "..."}
          value={busqueda.buscadorGeneral || ""}
          onChange={(e) => handleChange("buscadorGeneral", e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onFilter(busqueda)}
          className="pl-9 pr-4 py-1.5 text-sm rounded-lg bg-white text-gray-700 w-full sm:w-52 focus:outline-none focus:ring-2 focus:ring-blue-100"
          style={{ border: "1px solid #e5e7eb" }}
        />
      </div>
    </div>
  );
};

export default FiltrosProveedoresLegalia;
